import { Injectable } from '@angular/core'
import { HttpClient, HttpParams } from '@angular/common/http'
import { Observable, of } from 'rxjs'
import * as moment from 'moment'

import { catchError, concatMap, map, toArray } from 'rxjs/operators'
import * as R from 'ramda'
import * as lodash from 'lodash'

import { isSomething, Pagination } from '@tokeet-frontend/tv3-platform'
import {
  SearchExtraParams,
  ExtraView,
  ExtraResponse,
  CreateExtraPayload,
  UpdateExtraPayload,
  toExtraView,
} from './model'

@Injectable()
export class ExtraService {
  constructor(private http: HttpClient) {}

  all(searchParams?: SearchExtraParams, pagination?: Pagination): Observable<ExtraView[]> {
    const url = `@api/extras/retrieve/`

    pagination = pagination || { skip: 0, limit: 1000 }

    const filters: {
      due_from?: number
      due_to?: number
      method?: string
      status?: string | number
      rentals?: string | string[]
    } = {}

    if (searchParams && isSomething(searchParams)) {
      if (searchParams?.period?.from > 0 && searchParams?.period?.to > 0) {
        const dayStart = moment.unix(searchParams.period.from).startOf('day').unix()
        const dayEnd = moment.unix(searchParams.period.to).endOf('day').unix()

        filters.due_from = dayStart
        filters.due_to = dayEnd
      }
      if (searchParams?.method && searchParams?.method !== 'all') {
        filters.method = searchParams?.method
      }
      if (searchParams?.status && searchParams?.status !== 'all') {
        filters.status = searchParams?.status
      }
      if (isSomething(searchParams?.rentals)) {
        filters.rentals = searchParams?.rentals
      }
    }

    // sor by extra date
    const params = new HttpParams({
      fromObject: { skip: pagination.skip + '', limit: pagination.limit + '', sort: 'date', direction: '-1' },
    })

    return this.http
      .post<ExtraResponse[]>(url, { ...filters }, { params })
      .pipe(map((responses) => this.mapExtras(responses)))
  }

  get(id: string): Observable<ExtraView> {
    const url = `@api/extras/${id}`

    return this.http.get<ExtraResponse>(url).pipe(map((response) => toExtraView(response)))
  }

  getByIds(ids: string[]): Observable<ExtraView[]> {
    return of(...ids).pipe(
      concatMap((id) => this.get(id).pipe(catchError(() => of(null)))),
      toArray(),
      map((items) => items.filter((t) => !lodash.isNil(t)) as ExtraView[])
    )
  }

  create(request: CreateExtraPayload): Observable<ExtraView> {
    const url = '@api/extras/'

    return this.http.post<ExtraResponse>(url, request).pipe(map((response) => toExtraView(response)))
  }

  update(extraId: string, payload: UpdateExtraPayload): Observable<ExtraView> {
    const url = `@api/extras/${extraId}`

    return this.http.put<ExtraResponse>(url, payload).pipe(map((response) => toExtraView(response)))
  }

  delete(extraId: string) {
    const url = `@api/extras/${extraId}`

    return this.http.delete<ExtraResponse>(url)
  }

  deleteExtras(extraIds: string[]) {
    return of(...extraIds).pipe(
      concatMap((id) => this.delete(id)),
      toArray()
    )
  }

  private mapExtras(extras: ExtraResponse[]): ExtraView[] {
    return R.map((r: ExtraResponse) => toExtraView(r), extras)
  }
}
